import { collection, query, where, getDocs, orderBy, limit, DocumentData, Timestamp } from 'firebase/firestore';
import { db } from '../../../shared/utils/firebase';
import { whereLocation } from '../../auth/managedLocations';
import { toDate } from '../../../shared/utils/dateHelpers';
import { summarizeSuccessRecord, SuccessRecordKind } from '../../../shared/success/successRecords';

interface ClientSuccessMetric {
  id: string;
  client_id: string;
  client_name?: string;
  coach_name?: string;
  goal: string;
  goal_update: string;
  goal_achieved: boolean;
  target1: string;
  target2: string;
  target3: string;
  target1_update: string;
  target2_update: string;
  target3_update: string;
  target1_achieved: boolean;
  target2_achieved: boolean;
  target3_achieved: boolean;
  comments: string;
  created_by: string;
  created_by_uid: string;
  created_by_email: string;
  updated_at: Date;
  created_at: Date;
  kind: SuccessRecordKind;
  has_content: boolean;
  primary_location?: string;
  awaiting_first_update?: boolean;
  first_session_date?: Date;
}

interface ClientData {
  id: string;
  client_id: string;
  client_name: string;
  lead_coach_id: string;
  lead_coach_name: string;
  primary_location: string;
  active_programme: boolean;
  current_programme?: string;
  needs_success_update?: boolean;
  last_success_update?: any;
  first_session_date?: any;
  visit_dates?: any[];
}

interface SuccessDataCache {
  data: ClientSuccessMetric[];
  clients: ClientData[];
  timestamp: number;
  userRole: string;
  locations?: string[];
}

interface SuccessDataResult {
  successMetrics: ClientSuccessMetric[];
  clients: ClientData[];
}

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;
const cache = new Map<string, SuccessDataCache>();

export class SuccessDataService {
  private static getCacheKey(userRole: string, locations?: string[]): string {
    return `${userRole}-${locations?.join('|') || 'all'}`;
  }

  private static isValidCache(cacheEntry: SuccessDataCache): boolean {
    return Date.now() - cacheEntry.timestamp < CACHE_DURATION;
  }

  static async fetchClientSuccessData(
    userRole: string,
    assignedLocations?: string[],
    coachId?: string
  ): Promise<SuccessDataResult> {
    const cacheKey = this.getCacheKey(userRole, assignedLocations);
    const cachedData = cache.get(cacheKey);

    if (cachedData && this.isValidCache(cachedData)) {
      console.log('Returning cached success data');
      return {
        successMetrics: cachedData.data,
        clients: cachedData.clients
      };
    }

    try {
      // First, fetch all clients (both active and inactive) based on user role
      const clients = await this.fetchAllClients(userRole, assignedLocations, coachId);
      
      // Use batch querying for better performance
      const successMetrics = await this.fetchSuccessMetricsBatch(clients);
      
      // Cache the results
      cache.set(cacheKey, {
        data: successMetrics,
        clients: clients,
        timestamp: Date.now(),
        userRole,
        locations: assignedLocations
      });

      return {
        successMetrics,
        clients
      };
    } catch (error) {
      console.error('Error fetching client success data:', error);
      throw error;
    }
  }

  private static async fetchAllClients(
    userRole: string,
    assignedLocations?: string[],
    coachId?: string
  ): Promise<ClientData[]> {
    const clientsRef = collection(db, 'bigquery_cache/client_data/comprehensive_details');
    let clientsQuery;

    if (userRole === 'USER' && coachId) {
      // For coaches, only fetch their clients
      // Try with string coach_id first, then numeric if needed
      const coachIdStr = String(coachId);
      const coachIdNum = !isNaN(Number(coachId)) ? Number(coachId) : null;
      
      clientsQuery = query(
        clientsRef,
        where('lead_coach_id', '==', coachIdStr)
      );
    } else if (userRole === 'MANAGER' && assignedLocations?.length) {
      clientsQuery = query(
        clientsRef,
        whereLocation('primary_location', assignedLocations)
      );
    } else if (userRole === 'ADMIN') {
      clientsQuery = query(clientsRef);
    } else {
      // Default: return empty results
      clientsQuery = query(clientsRef, limit(0));
    }

    let clientsSnapshot = await getDocs(clientsQuery);
    
    // For USER role, if no results with string coach_id, try numeric
    if (userRole === 'USER' && coachId && clientsSnapshot.size === 0) {
      const coachIdNum = !isNaN(Number(coachId)) ? Number(coachId) : null;
      if (coachIdNum !== null) {
        console.log('No results with string coach_id, trying numeric:', coachIdNum);
        clientsQuery = query(
          clientsRef,
          where('lead_coach_id', '==', coachIdNum)
        );
        clientsSnapshot = await getDocs(clientsQuery);
      }
    }
    
    const clients: ClientData[] = [];

    clientsSnapshot.forEach((doc) => {
      const data = doc.data();
      const rawClientId = data.client_id ?? doc.id;
      const clientId = String(rawClientId);

      clients.push({
        id: doc.id,
        client_id: clientId,
        client_name: data.client_name,
        lead_coach_id: data.lead_coach_id,
        lead_coach_name: data.lead_coach_name,
        primary_location: data.primary_location,
        active_programme: data.active_programme,
        current_programme: data.current_programme,
        needs_success_update: data.needs_success_update,
        last_success_update: data.last_success_update,
        first_session_date: data.first_session_date,
        visit_dates: data.visit_dates
      });
    });
    

    return clients;
  }

  private static async fetchSuccessMetricsBatch(
    clients: ClientData[]
  ): Promise<ClientSuccessMetric[]> {
    const successMetrics: ClientSuccessMetric[] = [];
    const batchSize = 10; // Firestore 'in' query limit
    
    // Process clients in batches
    for (let i = 0; i < clients.length; i += batchSize) {
      const batch = clients.slice(i, i + batchSize);
      const clientIds = batch
        .map(c => c.client_id)
        .filter((id): id is string => Boolean(id));
      
      if (clientIds.length === 0) continue;
      
      // Create a map for quick client data lookup
      const clientMap = new Map<string, ClientData>();
      batch.forEach(client => {
        if (client.client_id) {
          clientMap.set(client.client_id, client);
        }
      });
      
      // Query success metrics for this batch
      const successRef = collection(db, 'client_success');
      const successQuery = query(
        successRef,
        where('client_id', 'in', clientIds),
        orderBy('created_at', 'desc')
      );
      
      const successSnapshot = await getDocs(successQuery);
      
      
      successSnapshot.forEach((doc) => {
        const data = doc.data();
        const rawMetricClientId = data.client_id ?? null;
        const metricClientId = rawMetricClientId !== null && rawMetricClientId !== undefined
          ? String(rawMetricClientId)
          : null;
        const clientData = metricClientId ? clientMap.get(metricClientId) : undefined;

        if (clientData && metricClientId) {
          // Classify from the raw doc: a stored `id` field is what marks an assessment seed.
          const summary = summarizeSuccessRecord(doc.id, data);
          const metric: ClientSuccessMetric = {
            id: doc.id,
            client_id: metricClientId,
            client_name: clientData.client_name,
            coach_name: clientData.lead_coach_name,
            primary_location: clientData.primary_location,
            goal: data.goal || '',
            goal_update: data.goal_update || '',
            goal_achieved: data.goal_achieved || false,
            target1: data.target1 || '',
            target2: data.target2 || '',
            target3: data.target3 || '',
            target1_update: data.target1_update || '',
            target2_update: data.target2_update || '',
            target3_update: data.target3_update || '',
            target1_achieved: data.target1_achieved || false,
            target2_achieved: data.target2_achieved || false,
            target3_achieved: data.target3_achieved || false,
            comments: data.comments || '',
            created_by: data.created_by || '',
            created_by_uid: data.created_by_uid || '',
            created_by_email: data.created_by_email || '',
            updated_at: toDate(data.updated_at) || toDate(data.created_at) || new Date(),
            created_at: toDate(data.created_at) || toDate(data.updated_at) || new Date(),
            kind: summary.kind,
            has_content: summary.hasContent,
            awaiting_first_update: false,
            first_session_date: toDate(clientData.first_session_date) || undefined
          };
          
          successMetrics.push(metric);
        }
      });
    }
    
    return successMetrics;
  }

  static clearCache(userRole?: string, locations?: string[]): void {
    if (userRole) {
      const cacheKey = this.getCacheKey(userRole, locations);
      cache.delete(cacheKey);
    } else {
      cache.clear();
    }
  }

  static async createBigQueryCache(): Promise<void> {
    // This method could be implemented to create a BigQuery cache collection
    // similar to bigquery_cache/client_data/comprehensive_details
    // for even better performance
    console.log('BigQuery cache creation not yet implemented');
  }
}

export type { ClientSuccessMetric, ClientData, SuccessDataResult };
